import React, {useState} from "react";
import css from "./index.module.scss";
import DefaultLayout from "layouts/default";
import Button from "@/components/button";
import { useUser} from "@/scripts/apis/site/useUser";
import { useRouter} from "next/router";
import authenticateSession from "scripts/auth/server/index.js";
import site from "/scripts/apis/site/index.js";
import ProductCard from "@/components/productCard";
import FloatingTextInput from "@/components/floating-text-input";
import { useRef} from "react";
import Selector from "@/components/selector";
import { useEffect} from "react";
import strapi from "@/scripts/apis/strapi/index.js";
import SegmentedControl from "@/components/segmentedControl";
import DiscreteProgressBar from "@/components/discreteProgressBar";
import FileInput from "@/components/fileInput";
import Loading from "@/components/loading";
import { StrapiPicture } from "@/components/picture";

export default function EditListingPage({tags, aiModels}) {
  const user = useUser({refreshInterval: 10000}, {});
  const router = useRouter();

  //only allow logged in users to access this page
  useEffect(() => {
    if (!user.loading && !user?.value?.id){
      router.push("/auth/signin");
    }
  }, [user]);


  const [listing, setListing] = useState(null);
  const [formDirty, setFormDirty] = useState(false);
  const [form, setForm] = useState({
    usage: useRef(),
    coverArt: useRef(),
    aiModel: useRef(),
    name: useRef(),
    price: useRef(),
    shortDescription: useRef(),
    longDescription: useRef(),
    tags: useRef(),
    prompt: useRef(),
    guidelines: useRef(),
    exampleText1: useRef(),
    exampleText2: useRef(),
    exampleText3: useRef(),
    exampleText4: useRef(),
    exampleImage1: useRef(),
    exampleImage2: useRef(),
    exampleImage3: useRef(),
    exampleImage4: useRef(),
    status: useRef(),
  });

  async function submit(){
    let allValid = true;
    for (const [id, ref] of Object.entries(form)){
      if (ref?.current && !await ref?.current?.isValid()){
        allValid = false;
      }
    }

    if (allValid){
      //setStatus("uploading");
      //upload cover art if it's changed
      let coverArtId = listing?.coverArt?.id;
      let coverArt = form?.coverArt?.current?.getValue();
      if (!coverArtId || coverArt.type){
        let coverArtFd = new FormData();
        coverArtFd.set("files", coverArt);
        let coverArtRes = await strapi.query(strapi.endpoints.UPLOAD_FILE, {
          body: coverArtFd,
        });
        coverArtId = coverArtRes[0].id;
      }


      //upload example images
      let exampleImageIds = listing?.exampleImages?.map((ex) => ex.id) || [];
      if (form?.exampleImage1?.current?.getValue()?.type){
        let fd = new FormData();
        fd.set("files", form?.exampleImage1?.current?.getValue());
        let exampleImageRes = await strapi.query(strapi.endpoints.UPLOAD_FILE, {
          body: fd,
        });
        exampleImageIds[0] = exampleImageRes[0].id;
      }
      if (form?.exampleImage2?.current?.getValue()?.type){
        let fd = new FormData();
        fd.set("files", form?.exampleImage2?.current?.getValue());
        let exampleImageRes = await strapi.query(strapi.endpoints.UPLOAD_FILE, {
          body: fd,
        });
        exampleImageIds[1] = exampleImageRes[0].id;
      }
      if (form?.exampleImage3?.current?.getValue()?.type){
        let fd = new FormData();
        fd.set("files", form?.exampleImage3?.current?.getValue());
        let exampleImageRes = await strapi.query(strapi.endpoints.UPLOAD_FILE, {
          body: fd,
        });
        exampleImageIds[2] = exampleImageRes[0].id;
      }
      if (form?.exampleImage4?.current?.getValue()?.type){
        let fd = new FormData();
        fd.set("files", form?.exampleImage4?.current?.getValue());
        let exampleImageRes = await strapi.query(strapi.endpoints.UPLOAD_FILE, {
          body: fd,
        });
        exampleImageIds[3] = exampleImageRes[0].id;
      }


      let exampleText = [form?.exampleText1?.current?.getValue(), form?.exampleText2?.current?.getValue(), form?.exampleText3?.current?.getValue(), form?.exampleText4?.current?.getValue()].filter((ex) => ex).join("\n\n\n");

      let listingRes = await strapi.query(strapi.endpoints.LISTINGS_ME_UPDATE, {
        data: {
          id: listing.id,
          name: form.name.current.getValue(),
          description: form.longDescription.current.getValue(),
          prompt: form.prompt.current.getValue(),
          guidelines: form.guidelines.current.getValue(),
          aiModel: form?.aiModel.current.getValue().value,
          price: Math.ceil(parseFloat(form.price.current.getValue()) * 100),
          coverArt: coverArtId,
          business: form.usage.current.getValue().value === "business",
          shortDescription: form.shortDescription.current.getValue(),
          exampleImages: exampleImageIds,
          tags: (form?.tags.current.getValue() || []).map((tag) => tag.value),
          exampleText,
          public: true,
        },
      });
      if (listingRes.error){
        console.error(listingRes.error);
      } else {
        router.push("/account/listings/create/thank-you");
      }
    }
  }

  async function makePrivate(){
    let listingRes = await strapi.query(strapi.endpoints.LISTINGS_ME_UPDATE, {
      data: {
        id: listing.id,
        public: false,
      },
    });
    if (!listingRes.error){
      setListing({
        ...listing,
        public: false,
      });
    }
  }
  async function makePublic(){
    let listingRes = await strapi.query(strapi.endpoints.LISTINGS_ME_UPDATE, {
      data: {
        id: listing.id,
        public: true,
      },
    });
    if (!listingRes.error){
      setListing({
        ...listing,
        public: true,
      });
    }
  }

  async function deleteListing(){
    let listingRes = await strapi.query(strapi.endpoints.LISTINGS_ME_DELETE, {
      data: {
        id: listing.id,
      },
    });
  }

  useEffect(() => {
    if (router.query.id){
      strapi.query(strapi.endpoints.LISTINGS_ME_FIND_ONE, {
        data: {id: router.query.id},
        populate: {
          coverArt: true,
          exampleImages: true,
          aiModel: true,
          tags: true,
        },
      }).then((res) => {
        if (!res.id){
          router.push("/404");
        } else {
          setListing(res);
        }
      });
    }
  }, [router]);


  const usageMessages = {
    "regular": "Regular prompts are for personal use, and typically involve simpler tasks or artistic images.",
    "business": "Enterprise-level prompts are longer and include detailed information on how to use them. They are also often tailored to specific business needs.",
  };

  const [usageState, setUsageState] = useState("regular");

  let exampleTexts = listing?.exampleText?.split("\n\n\n");

  return <DefaultLayout
    title="Edit Listing | Lazy Prompts"
    description="Update your Lazy Prompts listing."
    light
  >
    <section className={`light ${css.form}`}>
      <div className="sectionInner">
        <div className={css.stageWrapper}>
          <div className={`${css.editWrapper}`}>
            <h3>Edit Listing</h3>
            {listing && <>
              <div>Is your prompt designed for regular or enterprise usage?</div>
              <SegmentedControl
                onChange={(value) => {
                  setFormDirty(true);
                  setUsageState(value);
                }}
                segments={[{label: "Regular", value: "regular"}, {label: "Enterprise", value: "business"}]}
                ref={form.usage}
                helperText={usageMessages[usageState]}
                initialIndex={listing?.business ? 1 : 0}
              />

              <Selector
                maxOptions="50"
                onChange={() => setFormDirty(true)}
                options={aiModels.filter((model) => model.type === listing.aiModel.type)}
                noOptionsMessage="No AI models found."
                placeholder="Select an AI model"
                ref={form.aiModel}
                defaultValue={aiModels.find((m) => m.value === listing?.aiModel?.id)}
                validators={[
                  { test: (val) => !val, class: "invalid", message: "Required" },
                  { test: (val) => true, class: "valid", message: "" },
                ]}
              />


              <div className={css.priceRow}>
                <FloatingTextInput
                  className={css.name}
                  name="name"
                  label="Listing Name"
                  onChange={() => setFormDirty(true)}
                  light
                  initialValue={listing?.name}
                  autocomplete="off"
                  ref={form.name}
                  validators={[
                    { test: (val) => /^.{50}.+$/.test(val), class: "invalid", message: "Max length is 50 characters."},
                    { test: (val) => /[^a-zA-Z\d\s:]/.test(val), class: "invalid", message: "Only letters and numbers allowed."},
                    { test: (val) => /^$/.test(val), class: "invalid", message: "Required."},
                    { test: (val) => /^.{20}.+$/.test(val), class: "valid", message: "<20 characters recommended."},
                    { test: (val) => /^..*$/.test(val), class: "valid", message: ""},
                  ]}
                />
                <FloatingTextInput
                  className={css.price}
                  name="price"
                  label="Price (USD)"
                  onChange={() => setFormDirty(true)}
                  light
                  initialValue={listing?.price}
                  autocomplete="off"
                  prefixText="$"
                  ref={form.price}
                  validators={[
                    { test: (val) => /^$/.test(val), class: "invalid", message: "Required."},
                    {test: (val) => isNaN(val), class: "invalid", message: "Must be a number of the form X.XX"},
                    {
                      test: (val) => {
                        let cents = parseFloat(val, 10) * 100;
                        let minimum = 199;
                        if (listing.business){
                          minimum = 499;
                        }
                        return cents < minimum;
                      },
                      class: "invalid",
                      message: listing.business ? "Minimum price of $4.99" : "Minimum price of $1.99",
                    },
                    {test: (val) => /^([0-9]{2}[0-9]+\..*)$/.test(val), class: "invalid", message: "Max of 99.99"},
                    {test: (val) => /^([0-9]\.[0-9]{2}|[0-9]{2}\.[0-9]{2})$/.test(val), class: "valid", message: ""},
                    {test: (val) => /.*/, class: "invalid", message: "Must be a number of the form XX.XX"},
                  ]}
                />
              </div>
              <div className={css.row}>
                <FloatingTextInput
                  className={css.shortDescription}
                  name="shortDescription"
                  onChange={() => setFormDirty(true)}
                  label="Teaser Description"
                  light
                  multiline
                  initialValue={listing?.shortDescription}
                  autocomplete="off"
                  ref={form.shortDescription}
                  helperText="Shortest description displayed in the small product cards in search results."
                  validators={[
                    { test: (val) => /^$/.test(val), class: "invalid", message: "Required"},
                    { test: (val) => /^.{100}.+$/.test(val), class: "invalid", message: "Max 100 characters."},
                    { test: (val) => /^(.|\n)*$/.test(val), class: "valid", message: ""},
                  ]}
                />

                <FloatingTextInput
                  className={css.longDescription}
                  name="longDescription"
                  label="Full Description"
                  onChange={() => setFormDirty(true)}
                  light
                  autocomplete="off"
                  initialValue={listing?.description}
                  ref={form.longDescription}
                  multiline
                  helperText="Full prompt description displayed on the prompt's listing page."
                  validators={[
                    { test: (val) => /^$/.test(val), class: "invalid", message: "Required"},
                    { test: (val) => /^.{2000}.+$/.test(val), class: "invalid", message: "Max 2000 characters."},
                    { test: (val) => /^(.|\n)*$/.test(val), class: "valid", message: ""},
                  ]}
                />
              </div>
              <Selector
                maxOptions="50"
                options={tags}
                className={css.tags}
                isMulti
                onChange={() => setFormDirty(true)}
                noOptionsMessage="No tags found."
                placeholder="Select tags"
                ref={form.tags}
                defaultValue={ listing.tags.map((tag) => ({label: tag.name, value: tag.id}))}
                validators={[
                  { test: (val) => !val, class: "invalid", message: "Required" },
                  { test: (val) => val?.length === 0, class: "invalid", message: "Required" },
                  { test: (val) => val?.length < 3, class: "valid", message: "Adding 3+ tags is recommended" },
                  { test: (val) => true, class: "valid", message: "" },
                ]}
              />

              <div className={css.row}>
                <FloatingTextInput
                  className={css.prompt}
                  name="name"
                  label="Prompt String"
                  onChange={() => setFormDirty(true)}
                  light
                  autocomplete="off"
                  multiline
                  ref={form.prompt}
                  helperText="The full text of your AI prompt."
                  initialValue={listing?.prompt}
                  validators={[
                    { test: (val) => /^$/.test(val), class: "invalid", message: "Required."},
                    { test: (val) => /^(.|\n){0,100}$/.test(val), class: "invalid", message: "Min 100 characters."},
                    { test: (val) => /^(.|\n)*$/.test(val), class: "valid", message: ""},
                  ]}
                />
                <FloatingTextInput
                  className={css.guidelines}
                  name="name"
                  label="Usage Guidelines"
                  light
                  onChange={() => setFormDirty(true)}
                  autocomplete="off"
                  multiline
                  initialValue={listing?.guidelines}
                  helperText="Tips and tricks for using the prompt."
                  ref={form.guidelines}
                  validators={[
                    { test: (val) => /^$/.test(val), class: "invalid", message: "Required."},
                    {
                      test: (val) => {
                        if (listing.business){
                          return val.length < 500;
                        }
                        return val.length < 100;
                      },
                      class: "invalid",
                      message: listing.business ? "Min 500 characters" : "Min 100 characters",
                    },
                    { test: (val) => /^(.|\n)*$/.test(val), class: "valid", message: ""},
                  ]}
                />
              </div>

              <FileInput
                label="Featured Product Photo"
                onChange={() => setFormDirty(true)}
                ref={form.coverArt}
                validators={[
                  {test: (val) => val.size > 500000, class: "invalid", message: "Max of 500kb."},
                  {test: (val) => !val.type, class: "valid", message: ""},
                  {test: (val) => val.type === "image/jpg" || val.type === "image/jpeg" || val.type === "image/png", class: "valid", message: ""},
                  {test: (val) => true, class: "invalid", message: "Must be a JPG or PNG image."},
                ]}
                initialImageUrl={listing?.coverArt?.url}
              />

              {listing.aiModel?.type === "text" && <>
                <p>Provide four examples of your prompt&apos;s output. Replace your prompt string with [prompt] and prefix the AI output with [output]. These examples will be displayed on your listing&apos;s page to prospective customers.</p>
                <p>For example, suppose you are selling a prompt that summarizes a book specified by the user:</p>
                <pre className={css.example}>
                  <p>[prompt] Influence, by Cialdini.</p>
                  <p>[output]<br/>&quot;Influence: The Psychology of Persuasion&quot; is a book by social psychologist Robert Cialdini that explains the principles of influence and how they can be used to persuade people to comply with requests. The book identifies six universal principles of influence and is widely regarded as a classic in the field of social psychology.</p>
                </pre>
              </>}

              {listing.aiModel?.type === "visual" && <>
                <p>Provide four example images of your prompt&apos;s output. Each image should be a JPG or PNG, and be less than 500kb. These images will be displayed on your listing&apos;s page to prospective customers.</p>
              </>}

              {listing.aiModel?.type === "text" && <div className={css.examples}>
                <FloatingTextInput
                  className={css.exampleText}
                  onChange={() => setFormDirty(true)}
                  name="name"
                  label="Example 1"
                  light
                  autocomplete="off"
                  multiline
                  ref={form.exampleText1}
                  initialValue={exampleTexts[0] || "[prompt]\n\n[output]"}
                  validators={[
                    { test: (val) => /^$/.test(val), class: "invalid", message: "Required."},
                    { test: (val) => /^((?!\[prompt\]).)*$/s.test(val), class: "invalid", message: "Must have a [prompt] tag to indicate where the prompt string goes."},
                    { test: (val) => /^((?!\[output\]).)*$/s.test(val), class: "invalid", message: "Must have an [output] tag to indicate where the AI output starts."},
                    { test: (val) => /^.*\[prompt\].*\[output\].*$/.test(val), class: "invalid", message: "[prompt] and [output] tags must be on separate lines."},
                    { test: (val) => /^.*\[prompt\].*\n\[output\]/.test(val), class: "invalid", message: "[prompt] and [output] tags must be separated by one blank line."},
                    { test: (val) => /.+\[output\]/.test(val), class: "invalid", message: "[output] tag should be at the beginning of the line."},
                    { test: (val) => /\[output\].+/.test(val), class: "invalid", message: "[output] tag should be be the only text on its line."},
                    { test: (val) => /\[output\]\n\n/.test(val), class: "invalid", message: "There should be no blank lines immediately after [output]."},
                    { test: (val) => /\n\n\n/.test(val), class: "invalid", message: "Max of one blank line in a row."},
                    { test: (val) => /\[output\]\n.*/.test(val), class: "valid", message: ""},
                    { test: (val) => /(.|\n)*/.test(val), class: "invalid", message: "Required."},
                  ]}
                />
                <FloatingTextInput
                  className={css.exampleText}
                  name="name"
                  label="Example 2"
                  onChange={() => setFormDirty(true)}
                  light
                  autocomplete="off"
                  initialValue={exampleTexts[1] || "[prompt]\n\n[output]"}
                  multiline
                  ref={form.exampleText2}
                  validators={[
                    { test: (val) => /^$/.test(val), class: "invalid", message: "Required."},
                    { test: (val) => /^((?!\[prompt\]).)*$/s.test(val), class: "invalid", message: "Must have a [prompt] tag to indicate where the prompt string goes."},
                    { test: (val) => /^((?!\[output\]).)*$/s.test(val), class: "invalid", message: "Must have an [output] tag to indicate where the AI output starts."},
                    { test: (val) => /^.*\[prompt\].*\[output\].*$/.test(val), class: "invalid", message: "[prompt] and [output] tags must be on separate lines."},
                    { test: (val) => /^.*\[prompt\].*\n\[output\]/.test(val), class: "invalid", message: "[prompt] and [output] tags must be separated by one blank line."},
                    { test: (val) => /.+\[output\]/.test(val), class: "invalid", message: "[output] tag should be at the beginning of the line."},
                    { test: (val) => /\[output\].+/.test(val), class: "invalid", message: "[output] tag should be be the only text on its line."},
                    { test: (val) => /\[output\]\n\n/.test(val), class: "invalid", message: "There should be no blank lines immediately after [output]."},
                    { test: (val) => /\n\n\n/.test(val), class: "invalid", message: "Max of one blank line in a row."},
                    { test: (val) => /\[output\]\n.*/.test(val), class: "valid", message: ""},
                    { test: (val) => /(.|\n)*/.test(val), class: "invalid", message: "Required."},
                  ]}
                />
                <FloatingTextInput
                  className={css.exampleText}
                  name="name"
                  label="Example 3"
                  light
                  onChange={() => setFormDirty(true)}
                  autocomplete="off"
                  multiline
                  ref={form.exampleText3}
                  initialValue={exampleTexts[2] || "[prompt]\n\n[output]"}
                  validators={[
                    { test: (val) => /^$/.test(val), class: "invalid", message: "Required."},
                    { test: (val) => /^((?!\[prompt\]).)*$/s.test(val), class: "invalid", message: "Must have a [prompt] tag to indicate where the prompt string goes."},
                    { test: (val) => /^((?!\[output\]).)*$/s.test(val), class: "invalid", message: "Must have an [output] tag to indicate where the AI output starts."},
                    { test: (val) => /^.*\[prompt\].*\[output\].*$/.test(val), class: "invalid", message: "[prompt] and [output] tags must be on separate lines."},
                    { test: (val) => /^.*\[prompt\].*\n\[output\]/.test(val), class: "invalid", message: "[prompt] and [output] tags must be separated by one blank line."},
                    { test: (val) => /.+\[output\]/.test(val), class: "invalid", message: "[output] tag should be at the beginning of the line."},
                    { test: (val) => /\[output\].+/.test(val), class: "invalid", message: "[output] tag should be be the only text on its line."},
                    { test: (val) => /\[output\]\n\n/.test(val), class: "invalid", message: "There should be no blank lines immediately after [output]."},
                    { test: (val) => /\n\n\n/.test(val), class: "invalid", message: "Max of one blank line in a row."},
                    { test: (val) => /\[output\]\n.*/.test(val), class: "valid", message: ""},
                    { test: (val) => /(.|\n)*/.test(val), class: "invalid", message: "Required."},
                  ]}
                />
                <FloatingTextInput
                  className={css.exampleText}
                  name="name"
                  label="Example 4"
                  light
                  onChange={() => setFormDirty(true)}
                  autocomplete="off"
                  multiline
                  ref={form.exampleText4}
                  initialValue={exampleTexts[3] || "[prompt]\n\n[output]"}
                  validators={[
                    { test: (val) => /^$/.test(val), class: "invalid", message: "Required."},
                    { test: (val) => /^((?!\[prompt\]).)*$/s.test(val), class: "invalid", message: "Must have a [prompt] tag to indicate where the prompt string goes."},
                    { test: (val) => /^((?!\[output\]).)*$/s.test(val), class: "invalid", message: "Must have an [output] tag to indicate where the AI output starts."},
                    { test: (val) => /^.*\[prompt\].*\[output\].*$/.test(val), class: "invalid", message: "[prompt] and [output] tags must be on separate lines."},
                    { test: (val) => /^.*\[prompt\].*\n\[output\]/.test(val), class: "invalid", message: "[prompt] and [output] tags must be separated by one blank line."},
                    { test: (val) => /.+\[output\]/.test(val), class: "invalid", message: "[output] tag should be at the beginning of the line."},
                    { test: (val) => /\[output\].+/.test(val), class: "invalid", message: "[output] tag should be be the only text on its line."},
                    { test: (val) => /\[output\]\n\n/.test(val), class: "invalid", message: "There should be no blank lines immediately after [output]."},
                    { test: (val) => /\n\n\n/.test(val), class: "invalid", message: "Max of one blank line in a row."},
                    { test: (val) => /\[output\]\n.*/.test(val), class: "valid", message: ""},
                    { test: (val) => /(.|\n)*/.test(val), class: "invalid", message: "Required."},
                  ]}
                />
              </div>}

              {listing.aiModel.type === "visual" && <div className={css.examples}>
                <FileInput
                  label="Example Image 1"
                  onChange={() => setFormDirty(true)}
                  ref={form.exampleImage1}
                  validators={[
                    {test: (val) => val.size > 500000, class: "invalid", message: "Max of 500kb."},
                    {test: (val) => !val.type || val.type === "image/jpg" || val.type === "image/jpeg" || val.type === "image/png", class: "valid", message: ""},
                    {test: (val) => true, class: "invalid", message: "Must be a JPG or PNG image."},
                  ]}
                  initialImageUrl={(listing?.exampleImages || [])[0]?.url}
                />
                <FileInput
                  label="Example Image 2"
                  onChange={() => setFormDirty(true)}
                  ref={form.exampleImage2}
                  validators={[
                    {test: (val) => !val.type || val.type === "image/jpg" || val.type === "image/jpeg" || val.type === "image/png", class: "valid", message: ""},
                    {test: (val) => true, class: "invalid", message: "Must be a JPG or PNG image."},
                  ]}
                  initialImageUrl={(listing?.exampleImages || [])[1]?.url}
                />
                <FileInput
                  label="Example Image 3"
                  onChange={() => setFormDirty(true)}
                  ref={form.exampleImage3}
                  validators={[
                    {test: (val) => !val.type || val.type === "image/jpg" || val.type === "image/jpeg" || val.type === "image/png", class: "valid", message: ""},
                    {test: (val) => true, class: "invalid", message: "Must be a JPG or PNG image."},
                  ]}
                  initialImageUrl={(listing?.exampleImages || [])[2]?.url}
                />
                <FileInput
                  label="Example Image 4"
                  onChange={() => setFormDirty(true)}
                  ref={form.exampleImage4}
                  validators={[
                    {test: (val) => !val.type || val.type === "image/jpg" || val.type === "image/jpeg" || val.type === "image/png", class: "valid", message: ""},
                    {test: (val) => true, class: "invalid", message: "Must be a JPG or PNG image."},
                  ]}
                  initialImageUrl={(listing?.exampleImages || [])[3]?.url}
                />
              </div>}

              <div className={css.buttons}>
                {listing?.public && <Button label="Make Private" onClick={makePrivate} className={css.button} ghost/>}
                {!listing?.public && <Button label="Delete" onClick={deleteListing} className={css.button} ghost danger/>}
                {!listing?.public && <Button label="Make Public" onClick={makePublic} className={css.button} ghost/>}
                <Button label="Submit for Review" onClick={submit} className={css.button} disabled={!formDirty}/>
              </div>
            </>}
          </div>

        </div>
      </div>
    </section>

  </DefaultLayout>;
}


export async function getStaticProps() {
  let tags = [];

  let tagsRaw = [];
  let page = 0;
  do {
    tagsRaw = await strapi.query(strapi.endpoints.TAGS_FIND, {
      pagination: {
        page,
        pageSize: 100,
      },
      fields: ["id", "name"],
      populate: "*",
    });
    page ++;

    for (let tag of tagsRaw){
      tags.push({
        label: tag.name,
        value: tag.id,
      });
    }
  } while (tagsRaw?.length > 0);

  let aiModels = (await strapi.query(strapi.endpoints.AI_MODELS_FIND, {
    fields: ["id", "name", "version", "type"],
  })).map((model) => ({
    label: `${model.name} ${model.version > 0 ? model.version : ""}`,
    value: model.id,
    type: model.type,
  }));


  //user is logged in
  return {
    props: {
      tags, //note that tags is NOT the raw response from the api, but a transformed structure suitable for <Select/>
      aiModels, //also a transformed structure for <Select/>
    },
    revalidate: 60 * 60 * 24, //once a day
  };
}
